import { BAR_RECORD_VERSION, BAR_SLOTS, defaultBar, parseBarRecord, type BarRecord, type BarSlot, type Rules } from "odyssey-shared";
import { PAGE_TIMER, PENDING_TIMEOUT_MS, type BagTimer } from "../bag/snapshot.ts";

/**
 * The bar the player arranges, as the page holds it (P2.8-T29, ADR-121 decisions 2.4, 2.5, 3.5 and 5): the record the
 * server last sent, or null for the default bar, and the page's changes to it. Nothing is arranged before a `bar`
 * arrives in the session, nor while the account is frozen. The first change writes the default bar down (`defaultBar`,
 * the item slot's derived item its id), applies the change and sends the whole bar; a change is drawn at once. One set
 * is in flight at a time: a change made while it waits is drawn and sent with the next set once the answer comes, and
 * only the answer to the latest set replaces the page's bar with the server's. With no answer in `PENDING_TIMEOUT_MS`
 * the bar is sent again under a new seq. The seq counts from 1 and wraps past 65535 to 1.
 */

/**
 * What the bar draws from: whether a `bar` arrived in this session, and the record, null for the default bar.
 */
export interface ArrangedSnapshot {
  readonly arrived: boolean;
  readonly record: BarRecord | null;
}

/**
 * Sends a `barSet`: its seq and the ten slots, or null for the default bar. Answers whether it was sent.
 */
export type BarSetSender = (seq: number, slots: readonly BarSlot[] | null) => boolean;

/**
 * The largest seq; the next after it is 1.
 */
const MAX_SEQ = 0xffff;

/**
 * Nothing arrived yet.
 */
const NOTHING: ArrangedSnapshot = Object.freeze({ arrived: false, record: null });

/**
 * The page's copy of the player's bar.
 */
export class ArrangedBar {
  private snapshot: ArrangedSnapshot = NOTHING;
  private readonly listeners = new Set<() => void>();
  private send: BarSetSender = () => false;
  private seq = 0;
  private inFlight: number | null = null;
  private pending = false;
  private cancelWait: (() => void) | null = null;

  /**
   * @param rules The page's rules, which the default bar is derived from.
   * @param derivedItem The item the default bar's item slot shows now: the first consumable in the bag, or null.
   * @param frozen Whether a Game Master has frozen the account now.
   * @param timer Runs a callback after a delay, `setTimeout` on the page.
   */
  public constructor(
    private readonly rules: Rules,
    private readonly derivedItem: () => string | null,
    private readonly frozen: () => boolean,
    private readonly timer: BagTimer = PAGE_TIMER,
  ) {}

  /**
   * Hears each change of what the bar draws.
   *
   * @param listener Called after a change.
   * @returns Stops it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the bar draws from now.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): ArrangedSnapshot => this.snapshot;

  /**
   * Names what sends a `barSet`.
   *
   * @param send The sender.
   */
  public bindSend(send: BarSetSender): void {
    this.send = send;
  }

  /**
   * Whether the player may arrange the bar now: a `bar` arrived in this session and the account is not frozen.
   *
   * @returns True when a change would be taken.
   */
  public canArrange(): boolean {
    return this.snapshot.arrived && !this.frozen();
  }

  /**
   * Reads a `bar` from the server: a join's (seq 0) is the session's bar; an answer to the latest set in flight sends
   * the changes made meanwhile, or, with none, replaces the page's bar with the server's; any other is passed over.
   *
   * @param seq The set it answers, 0 for a join's.
   * @param slots The ten slots, or null for the default bar.
   */
  public receive(seq: number, slots: readonly BarSlot[] | null): void {
    if (seq === 0) {
      this.endWait();
      this.inFlight = null;
      this.pending = false;
      this.change({ arrived: true, record: recordOf(slots) });
      return;
    }
    if (!this.snapshot.arrived || seq !== this.inFlight) {
      return;
    }
    this.endWait();
    this.inFlight = null;
    if (this.pending) {
      this.pending = false;
      this.dispatch();
      return;
    }
    this.change({ arrived: true, record: recordOf(slots) });
  }

  /**
   * The slots as a change would start from: the record's, or the default bar written down.
   *
   * @returns A copy of the ten slots.
   */
  public current(): BarSlot[] {
    return [...(this.snapshot.record ?? defaultBar(this.rules, this.derivedItem())).slots];
  }

  /**
   * Puts a thing in a slot, replacing what it held.
   *
   * @param index The slot, from 0.
   * @param slot What it holds now.
   * @returns True when the bar changed.
   */
  public place(index: number, slot: BarSlot): boolean {
    return this.edit((slots) => {
      if (sameSlot(slots[index] ?? null, slot)) {
        return false;
      }
      slots[index] = slot;
      return true;
    });
  }

  /**
   * Exchanges two slots.
   *
   * @param a One slot, from 0.
   * @param b The other.
   * @returns True when the bar changed.
   */
  public swap(a: number, b: number): boolean {
    return this.edit((slots) => {
      const first = slots[a] ?? null;
      const second = slots[b] ?? null;
      if (a === b || sameSlot(first, second)) {
        return false;
      }
      slots[a] = second;
      slots[b] = first;
      return true;
    });
  }

  /**
   * Empties a slot.
   *
   * @param index The slot, from 0.
   * @returns True when the bar changed.
   */
  public clear(index: number): boolean {
    return this.place(index, null);
  }

  /**
   * Returns the bar to the default (Reset ability bar): sends null, and the bar derives as a new character's does.
   *
   * @returns True when an arranged bar was reset.
   */
  public resetToDefault(): boolean {
    if (!this.canArrange() || this.snapshot.record === null) {
      return false;
    }
    this.change({ arrived: true, record: null });
    this.changed();
    return true;
  }

  /**
   * Forgets the session's bar as the session ends: nothing is arranged or sent until the next join's `bar`.
   */
  public forget(): void {
    this.endWait();
    this.inFlight = null;
    this.pending = false;
    if (this.snapshot !== NOTHING) {
      this.change(NOTHING);
    }
  }

  /**
   * Applies an edit to a copy of the bar's slots, the default written down first, and sends the result.
   *
   * @param apply Changes the slots, answering whether it changed any.
   * @returns True when the bar changed.
   */
  private edit(apply: (slots: BarSlot[]) => boolean): boolean {
    if (!this.canArrange()) {
      return false;
    }
    const slots = this.current();
    if (!apply(slots)) {
      return false;
    }
    this.change({ arrived: true, record: { v: BAR_RECORD_VERSION, slots } });
    this.changed();
    return true;
  }

  /**
   * Sends the bar now, or after the set in flight is answered.
   */
  private changed(): void {
    if (this.inFlight !== null) {
      this.pending = true;
      return;
    }
    this.dispatch();
  }

  /**
   * Sends the bar as it stands under the next seq and waits for its answer.
   */
  private dispatch(): void {
    this.seq = this.seq >= MAX_SEQ ? 1 : this.seq + 1;
    const record = this.snapshot.record;
    if (!this.send(this.seq, record === null ? null : record.slots)) {
      return;
    }
    this.inFlight = this.seq;
    this.cancelWait = this.timer(() => {
      this.cancelWait = null;
      this.inFlight = null;
      this.pending = false;
      if (this.snapshot.arrived) {
        this.dispatch();
      }
    }, PENDING_TIMEOUT_MS);
  }

  /**
   * Stops waiting for an answer.
   */
  private endWait(): void {
    this.cancelWait?.();
    this.cancelWait = null;
  }

  /**
   * Replaces the snapshot and tells every listener.
   *
   * @param next The snapshot.
   */
  private change(next: ArrangedSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

/**
 * A record from the wire's slots, through the shared parser; null slots, or slots it cannot read, are the default bar.
 *
 * @param slots The slots, or null.
 * @returns The record, or null.
 */
function recordOf(slots: readonly BarSlot[] | null): BarRecord | null {
  if (slots === null || slots.length !== BAR_SLOTS) {
    return null;
  }
  const read = parseBarRecord({ v: BAR_RECORD_VERSION, slots });
  return read.ok ? read.record : null;
}

/**
 * Whether two slots hold the same thing.
 *
 * @param a One slot.
 * @param b The other.
 * @returns True for two empty slots or two of one kind and id.
 */
function sameSlot(a: BarSlot, b: BarSlot): boolean {
  return a === null || b === null ? a === b : a[0] === b[0] && a[1] === b[1];
}
